import React, { useCallback, useEffect, useState } from 'react';
import LabeledSelect from './labeled-select';
import { isGarageProduct, isUrbanPavilionProduct, useAppState } from 'state/appstate/appstate';
import { useNotificationStore } from 'state/notificationstate/notificationstate';
import { Parameter, Width } from 'api';
import * as viewerActions from 'viewer/viewerActions';
import ParameterCard from 'components/parameter-card/parameter-card';
import { Col, Divider, Row, Slider } from 'antd';
import Typography from 'helpers/fonts';
import { useViewerState } from 'state/viewer-state/viewer-state';
import {
  BarOpeningOption,
  BarOpeningSide,
  getAreaDetails,
  RailingOpeningPosition,
} from '@swd_admin/cdc-viewer';
import { ProductTitle } from 'components/product-title';
import { useTranslation } from 'react-i18next';
import { cmToFt } from 'helpers/util';
import { BARSIDE_ID, SANARA_ID, SURFSIDE_ID } from 'viewer/viewerActions';

const floorOptions = [
  { label: 'Yes', value: false },
  { label: 'No', value: true },
];

function correctMinMaxFoot({ min, max }: { min: number; max: number }): {
  min: number;
  max: number;
} {
  return { min: Math.ceil(min / 6) * 6, max: Math.floor(max / 6) * 6 };
}

const BuildingControls: React.FunctionComponent = () => {
  const { viewerState } = useViewerState();
  const { t } = useTranslation();
  const [widths, setWidths] = useState<Array<Width>>();
  const [railingOpeningPosition, setRailingOpeningPosition] =
    useState<RailingOpeningPosition>();
  const setNotification = useNotificationStore(
    (state) => state.setNotification
  );
  const AppState = useAppState((state) => ({
    openAreaSize: state.uState?.openAreaSize,
    style: state.styleProduct.style!,
    styleProduct: state.styleProduct.product!,
    uState: state.uState,
    setDepth: state.setDepth,
    setWidth: state.setWidth,
    setWallHeight: state.setWallHeight,
    setOverhang: state.setOverhang,
    setOpenAreaSize: state.setOpenAreaSize,
    setHasConcretePad: state.setHasConcretePad,
    updateExtraheightAndOverhang: state.updateExtraheightAndOverhang,
  }));
  const isGarage = isGarageProduct();
  const isUrbanPavilion = isUrbanPavilionProduct();
  const style = useViewerState((s) => s.viewerState?.style);

  const [enclosureAreaLength, setEnclosureAreaLength] = useState<number>(0);
  const [orientationIndex, setOrientationIndex] = useState<number | undefined>(
    undefined
  );
  const [openingSide, setOpeningSide] = useState<BarOpeningSide | 'none'>(
    undefined
  );
  const [openingOption, setOpeningOption] = useState<BarOpeningOption>(
    BarOpeningOption.None
  );

  useEffect(() => {
    if (
      viewerState?.style?.instances?.length &&
      AppState.style.id &&
      railingOpeningPosition === undefined &&
      ['17', '26', '31'].includes(AppState.style.id)
    ) {
      const railingOptionFrontOpening = viewerState.style.instances.find(
        ({ optionId }) => optionId === 'railOpening'
      );
      const railingOptionSideOpening = viewerState.style.instances.find(
        ({ optionId }) => optionId === 'railOpening_48'
      );
      if (railingOptionFrontOpening) {
        setRailingOpeningPosition(
          railingOptionFrontOpening?.localPosition?.u === 1
            ? RailingOpeningPosition.FrontRight
            : railingOptionFrontOpening?.localPosition?.u === 0
            ? RailingOpeningPosition.FrontLeft
            : RailingOpeningPosition.FrontCentre
        );
      } else if (railingOptionSideOpening) {
        const isCanmore = AppState.style.id === '17'; // canmore
        setRailingOpeningPosition(
          railingOptionSideOpening.parentId === 'railing_1' ||
            (!isCanmore && railingOptionSideOpening.parentId === 'railing_3')
            ? RailingOpeningPosition.SideLeft
            : RailingOpeningPosition.SideRight
        );
      } else {
        console.warn('no initial railing opening!');
      }
    }
  }, [viewerState?.style?.instances?.length, AppState.style.id]);

  useEffect(() => {
    if (
      AppState?.style?.id &&
      AppState?.uState?.dimensions?.width &&
      AppState.uState?.dimensions?.depth
    ) {
      AppState.updateExtraheightAndOverhang(
        AppState?.style?.id,
        AppState.uState.dimensions.width,
        AppState.uState.dimensions.depth
      );
    }
  }, [
    AppState?.style?.id,
    AppState.uState?.dimensions?.width,
    AppState.uState?.dimensions?.depth,
  ]);

  useEffect(() => {
    if (!enclosureAreaLength && style) {
      const { interiorAreaLength, openAreaLength } = getAreaDetails(style);
      setEnclosureAreaLength(interiorAreaLength);
      viewerActions.setOpenAreaLength(openAreaLength);
    }

    if (orientationIndex === undefined && style) {
      const { wallOffsetIndex } = getAreaDetails(style);
      setOrientationIndex(wallOffsetIndex);
    }
  }, [style]);

  useEffect(() => {
    setOpeningSide(style?.barOpeningSide ?? 'none');
  }, [style?.barOpeningSide]);

  useEffect(() => {
    setOpeningOption(style?.barOpeningOption ?? BarOpeningOption.None);
  }, [style?.barOpeningOption]);

  useEffect(() => {
    if (style) {
      const { maxLength } = getAreaDetails(style);
      if (
        AppState.openAreaSize &&
        enclosureAreaLength !== maxLength - AppState.openAreaSize
      ) {
        setEnclosureAreaLength(maxLength - AppState.openAreaSize);
      }
    }
  }, [AppState.openAreaSize]);

  const handleChangeOpenAreaLength = useCallback(
    (value) => {
      if (value && style) {
        const { maxLength } = getAreaDetails(style);
        AppState.setOpenAreaSize(maxLength - value);
        viewerActions.setOpenAreaLength(maxLength - value);
      }
    },
    [style]
  );

  const getMinMaxForStyle = useCallback(
    (styleId: string) => {
      if (!style) return;

      const { maxLength } = getAreaDetails(style);
      if (styleId === BARSIDE_ID || styleId === SURFSIDE_ID) {
        return correctMinMaxFoot({
          min: maxLength / 3,
          max: maxLength - 36,
        });
      }

      if (styleId === SANARA_ID) {
        return correctMinMaxFoot({
          min: 36,
          max: maxLength / 2,
        });
      }
    },
    [style]
  );

  const getOrientations = useCallback(() => {
    if (!style) return { right: '1', left: '3' };

    const { wallOffsetIndex } = getAreaDetails(style);
    return {
      right: Math.min(wallOffsetIndex, (wallOffsetIndex + 2) % 4).toString(),
      left: Math.max(wallOffsetIndex, (wallOffsetIndex + 2) % 4).toString(),
    };
  }, [style]);

  useEffect(() => {
    if (style && AppState.style.id) {
      const { interiorAreaLength, openAreaLength, maxLength } =
        getAreaDetails(style);
      const minMax = getMinMaxForStyle(AppState.style.id);
      if (interiorAreaLength !== enclosureAreaLength && minMax) {
        const { max, min } = minMax;
        const enclosureArea =
          interiorAreaLength >= min ? interiorAreaLength : max;
        setEnclosureAreaLength(enclosureArea);
        viewerActions.setOpenAreaLength(maxLength - enclosureArea);
      }
    }
  }, [
    AppState?.style?.id,
    AppState.uState?.dimensions?.width,
    AppState.uState?.dimensions?.depth,
  ]);

  useEffect(() => {
    if (!AppState.style || !AppState.style.depths) {
      return;
    }
    const widths = AppState.style.depths.find(
      (d) => d.depth.value === AppState.uState?.dimensions?.depth
    )?.widths;
    if (!widths) {
      setNotification(
        'No widths exist for the selected depth',
        'error',
        'small'
      );
      return;
    }
    setWidths(widths);
  }, [
    AppState.style,
    AppState.uState?.dimensions?.depth,
    setNotification,
    AppState.uState,
  ]);

  if (!AppState.uState || !AppState.style) {
    return <></>;
  }

  const separatePriceByHyphen = (s: string): string => {
    return (
      s &&
      `${s?.split(' ').slice(0, -1)?.join(' ')} - ${s?.split(' ').slice(-1)}`
    );
  };

  return (
    <div style={{ padding: '16px' }}>
      <ProductTitle />

      <Row gutter={[16, 0]}>
        <Col span={12}>
          {AppState.style.depths && (
            <div style={{ width: '100%' }}>
              <LabeledSelect
                onSelect={(v) => AppState.setDepth(v as number)}
                selected={AppState.uState!.dimensions.depth}
                options={AppState.style.depths.map((d) => {
                  return { label: d.depth.label, value: d.depth.value };
                })}
                title="buildingControls.depth"
              />
            </div>
          )}
        </Col>
        <Col span={12}>
          {AppState.style.depths && widths && (
            <div style={{ width: '100%' }}>
              <LabeledSelect
                onSelect={(v) => {
                  AppState.setWidth(v as number);
                }}
                selected={AppState.uState!.dimensions.width}
                options={widths.map((w) => {
                  return { label: w.label?.split(' ')[0], value: w.value };
                })}
                title="buildingControls.width"
              />
            </div>
          )}
        </Col>
        <Col span={24} style={{ margin: '8px 0' }}>
          <Row justify="end">
            <Typography styleid={6}>
              {
                AppState.style.depths?.find(
                  ({ depth: { value } }) =>
                    value === AppState.uState!.dimensions.depth
                )?.depth?.label
              }{' '}
              x{' '}
              {separatePriceByHyphen(
                widths?.find(
                  ({ value }) => value === AppState.uState!.dimensions.width
                )?.label || ''
              )}
            </Typography>
          </Row>
        </Col>
      </Row>

      {AppState.style.wallHeights && (
        <> 
        <Typography styleid={7}>
          {t('buildingControls.info1')}
         </Typography>
        <div style={{ display: 'flex', width: '100%', margin: '24px 0' }}>
          <LabeledSelect
            onSelect={(v) => AppState.setWallHeight(v as number)}
            selected={AppState.uState!.dimensions.wallHeight}
            options={AppState.style.wallHeights.map((w) => {
              return { label: separatePriceByHyphen(w.label), value: w.value };
            })}

            title={isUrbanPavilion ? "buildingControls.postHeight" : "buildingControls.wallHeight"}
          />
        </div>
        </>
      )}
      {AppState.style.overhangs && (
        <>
        <div style={{ display: 'flex', width: '100%', margin: '24px 0' }}>
          <LabeledSelect
            onSelect={(v) => AppState.setOverhang(v as number)}
            selected={AppState.uState!.overhang}
            options={AppState.style.overhangs.map((o) => {
              return { label: separatePriceByHyphen(o.label), value: o.value };
            })}
            title="buildingControls.overhang"
          />
        </div>
      { isUrbanPavilion && (AppState.uState!.dimensions.width >= 180 || AppState.uState!.dimensions.depth >= 180) && <Typography styleid={7}>
          {t('buildingControls.info2')}
         </Typography>
         }
        </>
      )}

      {!isGarage && !isUrbanPavilion && viewerState?.style?.renderParameters && (
        <div style={{ display: 'flex', width: '100%', margin: '24px 0' }}>
          <LabeledSelect
            onSelect={(value) => {
              viewerActions.setConcretePadOption(value as boolean);
              AppState.setHasConcretePad(value as boolean);
            }}
            selected={AppState.uState!.hasConcretePad}
            options={floorOptions}
            title="buildingControls.floor"
          />
        </div>
      )}

      {style &&
        ['17', '26', '31'].includes(AppState.style.id) &&
        railingOpeningPosition !== undefined && (
          <div style={{ display: 'flex', width: '100%', margin: '24px 0' }}>
            <LabeledSelect
              onSelect={(value) => {
                viewerActions.changRailingOpeningPosition(
                  value as RailingOpeningPosition
                );
                setRailingOpeningPosition(value as RailingOpeningPosition);
              }}
              selected={railingOpeningPosition}
              options={[
                {
                  label: 'Front centre',
                  value: RailingOpeningPosition.FrontCentre,
                },
                {
                  label: 'Front right',
                  value: RailingOpeningPosition.FrontRight,
                },
                {
                  label: 'Front left',
                  value: RailingOpeningPosition.FrontLeft,
                },
                {
                  label: 'Right side',
                  value: RailingOpeningPosition.SideRight,
                },
                { label: 'Left side', value: RailingOpeningPosition.SideLeft },
              ]}
              title="buildingControls.railingOpeningPosition"
            />
          </div>
        )}

      {/* annex */}
      {style && AppState.style.id === '84' && (
        <div style={{ width: '100%', margin: '24px 0' }}>
          <ParameterCard
            onChange={() => viewerActions.changeAnnexWallOpening()}
            parameter={{
              name: 'Wall opening',
              key: '',
              defaultValue:
                +(
                  style.instances.find(({ id }) => id === 'personalAccessDoor')
                    ?.localPosition?.u || 0
                ) < 0.5
                  ? 'right'
                  : 'left',
              description: '',
              tooltip: '',
              max: null,
              min: null,
              options: [
                { label: 'Left', value: 'left' },
                { label: 'Right', value: 'right' },
              ],
              type: Parameter.type.SELECT,
            }}
          />
        </div>
      )}

      {style &&
        [BARSIDE_ID, SURFSIDE_ID, SANARA_ID].includes(AppState.style.id) && (
          <div style={{ width: '100%', margin: '24px 0' }}>
            <Divider
              style={{
                borderColor: `${Theme['@grey-normal']}`,
                margin: '30px 0',
              }}
            />

            {getMinMaxForStyle(AppState.style.id)!.min <
              getMinMaxForStyle(AppState.style.id)!.max && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 12 }}>
                  <Typography styleid={6}>
                    {t('buildingControls.enclosureSize')}
                  </Typography>
                </div>
                <Row>
                  <Col span={12}>
                    <Slider
                      tipFormatter={(v) => cmToFt(v!)}
                      min={getMinMaxForStyle(AppState.style.id)!.min}
                      max={getMinMaxForStyle(AppState.style.id)!.max}
                      onChange={setEnclosureAreaLength}
                      onAfterChange={handleChangeOpenAreaLength}
                      value={enclosureAreaLength}
                      step={6}
                    />
                  </Col>
                  <Col
                    span={4}
                    style={{ alignSelf: 'center', marginLeft: '16px' }}
                  >
                    <Typography styleid={6}>
                      {cmToFt(enclosureAreaLength)}
                    </Typography>
                  </Col>
                </Row>
              </div>
            )}

            {orientationIndex !== undefined &&
              [SURFSIDE_ID, SANARA_ID].includes(AppState.style.id) && (
                <ParameterCard
                  onChange={(value) => {
                    viewerActions.changeOpenAreaOrientation();
                    setOrientationIndex(value);
                  }}
                  parameter={{
                    name: 'Enclosure orientation',
                    key: '',
                    defaultValue: orientationIndex?.toString(),
                    description: '',
                    tooltip: '',
                    max: null,
                    min: null,
                    options: [
                      { label: 'Left side', value: getOrientations().left },
                      { label: 'Right side', value: getOrientations().right },
                    ],
                    type: Parameter.type.SELECT,
                  }}
                />
              )}

            {AppState.style.id === SURFSIDE_ID && (
              <ParameterCard
                onChange={(value) => {
                  viewerActions.changeBarOpeningSide(
                    value === 'none' ? undefined : value
                  );
                }}
                parameter={{
                  name: 'Opening side',
                  key: '',
                  defaultValue: openingSide,
                  description: '',
                  tooltip: '',
                  max: null,
                  min: null,
                  options: [
                    {
                      label: 'Front',
                      value:
                        orientationIndex?.toString() === getOrientations().left
                          ? 'left'
                          : 'right',
                    },
                    {
                      label: 'Back',
                      value:
                        orientationIndex?.toString() === getOrientations().left
                          ? 'right'
                          : 'left',
                    },
                    { label: 'None', value: 'none' },
                  ],
                  type: Parameter.type.SELECT,
                }}
              />
            )}

            {AppState.style.id === BARSIDE_ID && (
              <ParameterCard
                onChange={(value) => {
                  viewerActions.changeBarOpeningSide(
                    value === 'none' ? undefined : value
                  );
                }}
                parameter={{
                  name: 'Opening side',
                  key: '',
                  defaultValue: openingSide,
                  description: '',
                  tooltip: '',
                  max: null,
                  min: null,
                  options: [
                    { label: 'Left', value: 'left' },
                    { label: 'Right', value: 'right' },
                    { label: 'None', value: 'none' },
                  ],
                  type: Parameter.type.SELECT,
                }}
              />
            )}

            {orientationIndex !== undefined &&
              [SURFSIDE_ID, BARSIDE_ID].includes(AppState.style.id) &&
              openingSide !== 'none' && (
                <ParameterCard
                  onChange={() => viewerActions.toggleBarOpeningOption()}
                  parameter={{
                    name: 'Opening option',
                    key: '',
                    defaultValue: openingOption,
                    description: '',
                    tooltip: '',
                    max: null,
                    min: null,
                    options: [
                      { label: 'Door', value: BarOpeningOption.Door },
                      { label: 'None', value: BarOpeningOption.None },
                    ],
                    type: Parameter.type.SELECT,
                  }}
                />
              )}
          </div>
        )}
    </div>
  );
};

export default BuildingControls;
